import React, { useEffect, useRef } from 'react'

import {
  Box,
  Color,
  FontFamily,
  FontWeight,
  Heading2,
  Link,
  LinkVariant,
  Space,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { HOMEPAGE_REVIEWS } from './config'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'
import { CWCAsFeaturedInStrip } from './components/CWCAsFeaturedInStrip'
import { CWCRecipeVideosCarousel } from './components/CWCRecipeVideosCarousel'
import { CWCReviewsCarousel } from './components/CWCReviewsCarousel'

type TrustPilotType = {
  loadFromElement: (element: HTMLElement | null, force: boolean) => void
}
export type TrustpilotWindow = typeof window & { Trustpilot: TrustPilotType }

const AccentSpan = withCitrusTheme('span', (theme) => ({
  display: 'inline',
  color: theme.color(Color.Primary_600),
}))

// The recipe-video cards' drop shadow stays faintly visible ~73px below them, and the video
// carousel pads its slides so the full gradual fade isn't clipped — leaving the carousel box
// ending 84px below the cards. Figma's card-to-divider gap is tighter than that (24px mobile per
// designer, 48px tablet, 40px desktop) with the shadow overlapping the divider, so pull the
// "As featured in" strip up over the shadow to hit each breakpoint's gap (gap − 84px).
const AsFeaturedInWrapper = withCitrusTheme('div', (theme) => ({
  marginTop: '-60px',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    marginTop: '-36px',
  },
  [`@media (min-width: ${theme.tokens.breakpoints[2]})`]: {
    marginTop: '-44px',
  },
}))

export function CWCHomepageReviewsSection() {
  const ref = useRef(null)

  useEffect(() => {
    const win = window as TrustpilotWindow
    // widget may not load if the bootstrapper script has been loaded before the component gets rendered
    if (win.Trustpilot) {
      win.Trustpilot.loadFromElement(ref.current, true)
    }
  }, [])

  const trackClickTrustpilotCTA = () => {
    trackCTAClick(
      TrackingActions.HOMEPAGE_REVIEWS_TRUSTPILOT_CTA,
      LandingPageContentSections.HOMEPAGE_REVIEWS,
    )
  }

  return (
    <section data-testid="homepageReviewsSection">
      <Box paddingV={[14, null, null, 20]}>
        <Heading2
          data-testid="homepage-reviews-header"
          size={13}
          color={Color.ColdGrey_900}
          fontFamily={FontFamily.ExtraBold}
          fontWeight={FontWeight.Bold}
          textAlign={TextAlign.Center}
        >
          {HOMEPAGE_REVIEWS.headingLine1}
          <br />
          <AccentSpan>{HOMEPAGE_REVIEWS.headingLine2Accent}</AccentSpan>
          {HOMEPAGE_REVIEWS.headingLine2Rest}
        </Heading2>
        <Space size={4} />
        <Box style={{ position: 'relative' }}>
          <Box
            ref={ref}
            data-testid="trustpilot-widget"
            data-locale="en-GB"
            data-template-id="5419b6a8b0d04a076446a9ad"
            data-businessunit-id="512cda6e000064000522fb6a"
            data-style-height="27px"
            data-style-width="100%"
            data-theme="light"
            data-review-languages="en"
            data-font-family="Montserrat"
            style={{ fontSize: '16px', lineHeight: '24px' }}
          />
          <Link
            data-testid="trustpilot-link"
            href="https://uk.trustpilot.com/review/gousto.co.uk"
            target="_blank"
            rel="noopener"
            onClick={trackClickTrustpilotCTA}
            aria-label="Open Gousto reviews on Trustpilot"
            style={{ position: 'absolute', inset: 0, display: 'block' }}
            variant={LinkVariant.None}
          />
        </Box>
        <Space size={6} />
        <CWCReviewsCarousel />
        <Space size={6} />
        <CWCRecipeVideosCarousel />
        <AsFeaturedInWrapper>
          <CWCAsFeaturedInStrip />
        </AsFeaturedInWrapper>
      </Box>
    </section>
  )
}
